import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../../config';

export type UserEventSlackSetting = {
  id: string;
  eventType: string;
  slackEnabled: boolean;
  updatedAt: string;
  updatedBy: string | null;
  createdAt: string;
};

export function useUserEventSlackSettingsQuery() {
  return useQuery({
    queryKey: ['superadmin-user-event-slack-settings'],
    queryFn: async (): Promise<{ ok: boolean; data: UserEventSlackSetting[] }> => {
      const res = await fetch(`${API_URL}/superadmin/user-events/slack-settings`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch Slack settings');
      return res.json();
    },
  });
}

export function useToggleUserEventSlackMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { eventType: string; slackEnabled: boolean }) => {
      const res = await fetch(
        `${API_URL}/superadmin/user-events/slack-settings/${encodeURIComponent(input.eventType)}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ slackEnabled: input.slackEnabled }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false) {
        throw new Error(data?.error ?? 'Failed to toggle Slack setting');
      }
      return data as { ok: boolean; data: UserEventSlackSetting };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-user-event-slack-settings'] }).catch(() => {});
    },
  });
}

export type UserEventChannelSettings = {
  channelId: string | null;
  channelName: string | null;
  connected: boolean;
  internalOrgId: string | null;
  internalOrgName: string | null;
};

export type SlackChannel = { id: string; name: string; isPrivate: boolean };

export function useUserEventChannelSettingsQuery() {
  return useQuery({
    queryKey: ['superadmin-user-event-channel-settings'],
    queryFn: async (): Promise<{ ok: boolean; data: UserEventChannelSettings }> => {
      const res = await fetch(`${API_URL}/superadmin/user-events/channel-settings`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch channel settings');
      return res.json();
    },
  });
}

export function useUserEventChannelListQuery(enabled: boolean) {
  return useQuery({
    queryKey: ['superadmin-user-event-channels'],
    enabled,
    queryFn: async (): Promise<{ ok: boolean; data: SlackChannel[] }> => {
      const res = await fetch(`${API_URL}/superadmin/user-events/channels`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to list channels');
      return res.json();
    },
  });
}

export function useUpdateUserEventChannelMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { channelId: string; channelName: string }) => {
      const res = await fetch(`${API_URL}/superadmin/user-events/channel-settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false) {
        throw new Error(data?.error ?? 'Failed to update channel');
      }
      return data;
    },
    onSuccess: () => {
      queryClient
        .invalidateQueries({ queryKey: ['superadmin-user-event-channel-settings'] })
        .catch(() => {});
    },
  });
}

export function useTestUserEventChannelMutation() {
  return useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/superadmin/user-events/channel-test`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false) {
        throw new Error(data?.error ?? 'Test failed');
      }
      return data;
    },
  });
}
